import { t } from "@lingui/core/macro";

import {
  type DateTimeFormatOptions,
  type DateTimeRangeFormatOptions,
  type RelativeDateTimeOptions,
  dateOnlyString,
  dateTimeString,
  isSameDay,
  relativeDateTimeString,
  timeOnlyString,
} from "_/utils/datetime";
import {
  NON_BREAKING_DASH,
  NON_BREAKING_SPACE,
  UNKNOWN_VALUE_STRING,
} from "_/utils/consts";

import * as S from "./styled";

// Components for rendering dates and times. The string formatting they use
// lives in `_/utils/datetime`, which can also be used directly when a plain
// string is needed.

/**
 * Display a date and time in ISO 8601 format (YYYY-MM-DD, e.g. 2024-01-24
 * 14:52) that's the standard format in Basis, in the browser's time zone
 * unless `timeZone` says otherwise.
 */
export const DateTime = (props: DateTimeFormatOptions) => {
  const nowrap = props.nowrap ?? "date-only";
  const nonBreaking = ["all", "break-range"].includes(nowrap);
  return (
    <S.DateTimeSpan $nonBreaking={nonBreaking}>
      <DateOnly {...props} /> <TimeOnly {...props} />
    </S.DateTimeSpan>
  );
};

type DateOnlyProps = Omit<DateTimeFormatOptions, "precision">;

/**
 * Display a date (only) in ISO 8601 format (YYYY-MM-DD, e.g. 2024-01-24)
 * that's the standard format in Basis, in the browser's time zone unless
 * `timeZone` says otherwise.
 */
export const DateOnly = (props: DateOnlyProps) => {
  // UI components prevent wrapping with CSS, not Unicode characters
  const nowrap = props.nowrap ?? "date-only";
  const nonBreaking = ["all", "break-range", "date-only"].includes(nowrap);
  return (
    <S.DateTimeSpan $nonBreaking={nonBreaking}>
      {dateOnlyString({ ...props, nowrap: "none" })}
    </S.DateTimeSpan>
  );
};

type TimeOnlyProps = Omit<DateTimeFormatOptions, "nowrap">;

/**
 * Display a time (only) in 24-hour time format (HH:mm, e.g. 14:52) which is the
 * standard format in Basis, in the browser's time zone unless `timeZone` says
 * otherwise.
 */
export const TimeOnly = (props: TimeOnlyProps) => <>{timeOnlyString(props)}</>;

/**
 * Display a date/time range in ISO 8601 format. If start and end dates are the
 * same, then omit the second date. Otherwise show both dates. It's the caller's
 * responsibility to ensure that `start` is before `end`.
 *
 * Examples: "2024-01-24 14:52 - 15:52", "2024-01-24 14:52 - 2024-01-24 15:23"
 */
export const DateTimeRange = ({
  start,
  end,
  defaultValue,
  nowrap = "break-range",
  precision,
  timeZone,
}: DateTimeRangeFormatOptions) => {
  if (start == null) {
    return defaultValue ?? UNKNOWN_VALUE_STRING;
  }
  const space = ["all", "break-range"].includes(nowrap)
    ? NON_BREAKING_SPACE
    : " ";
  const rangeDelimiter = `${space}${NON_BREAKING_DASH}${space}`;
  const startJsx = (
    <DateTime
      value={start}
      defaultValue={defaultValue}
      nowrap={nowrap}
      timeZone={timeZone}
    />
  );
  let endJsx: React.ReactNode = null;
  if (end != null) {
    endJsx = isSameDay(start, end, timeZone) ? (
      <TimeOnly value={end} precision={precision} timeZone={timeZone} />
    ) : (
      <DateTime value={end} nowrap={nowrap} timeZone={timeZone} />
    );
  }
  return (
    <>
      {startJsx}
      {rangeDelimiter}
      {endJsx}
    </>
  );
};

/**
 * Render a relative time string (e.g. "2 hours ago") from the current time.
 *
 * To show "Now" instead of of a relative time for recent events, use the
 * `isNow` prop.
 *
 * A tooltip will be added to the rendered string showing the actual time in both
 * local time and ISO 8601 format.
 */
export const RelativeDateTime = (props: RelativeDateTimeOptions) => {
  const relativeDateTime = relativeDateTimeString(props);
  const { value } = props;

  let tooltip: string | undefined = undefined;
  if (value != null) {
    const iso8601 = new Date(value).toISOString();
    const localDateTime = dateTimeString({
      value,
      nowrap: "all", // OK because not copy-able to clipboard
    });
    tooltip = t`common.local-time-and-utc ${localDateTime} ${iso8601}`;
  }

  return <span title={tooltip}>{relativeDateTime}</span>;
};
